import React, { useState, useMemo } from 'react';
import {
  LayoutDashboard,
  ArrowLeftRight,
  Repeat,
  TrendingUp,
  CalendarDays,
  BarChart3,
  HelpCircle,
  BookOpen,
  Info,
  Lightbulb,
  CheckCircle2,
  ChevronRight,
  Search,
  RefreshCw,
  Database,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { usePermissions } from '../../hooks/usePermissions';
import { HELP_CONTENT } from '../../data/help-content';
import type { HelpPageContent, HelpKeyFeature, HelpStep, HelpFaqItem } from '../../types/help';
import type { Permission } from '../../types/permissions';
import { cn } from '../../lib/utils';

// ── Bold markdown renderer ────────────────────────────────────────
// Renders **text** as <strong> for clickable UI element labels per
// Access User Guide Writing Standards (bold = instruction to click).
const renderBold = (text: string): React.ReactNode => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

// ── Icon map ─────────────────────────────────────────────────────
const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  LayoutDashboard,
  ArrowLeftRight,
  Repeat,
  TrendingUp,
  CalendarDays,
  BarChart3,
  HelpCircle,
  RefreshCw,
  Database,
};

// ── Note callout ─────────────────────────────────────────────────
const NoteCallout: React.FC<{ text: string }> = ({ text }) => (
  <div className="flex items-start gap-2 mt-2 p-2.5 rounded-md bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 text-sm text-blue-800 dark:text-blue-300">
    <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
    <span>{renderBold(text)}</span>
  </div>
);

// ── Tip callout ───────────────────────────────────────────────────
const TipCallout: React.FC<{ text: string }> = ({ text }) => (
  <div className="flex items-start gap-2 mt-2 p-2.5 rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 text-sm text-amber-800 dark:text-amber-300">
    <Lightbulb className="h-3.5 w-3.5 mt-0.5 shrink-0" />
    <span>{renderBold(text)}</span>
  </div>
);

// ── Props ────────────────────────────────────────────────────────
interface HelpDrawerProps {
  open: boolean;
  onClose: () => void;
  currentPage: string;
  onNavigate: (pageKey: string) => void;
}

// ── Component ────────────────────────────────────────────────────
export const HelpDrawer: React.FC<HelpDrawerProps> = ({
  open,
  onClose,
  currentPage,
  onNavigate,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const { hasPermission } = usePermissions();

  // Get content for the current page (fall back to a generic placeholder)
  const content: HelpPageContent | null = HELP_CONTENT[currentPage] ?? null;

  // Permission-aware filter helper
  const canShow = (requiredPermission?: Permission): boolean => {
    if (!requiredPermission) return true;
    return hasPermission(requiredPermission);
  };

  // Filtered helpers (apply search + permission gate together)
  const filterText = searchQuery.toLowerCase().trim();

  const filteredFeatures = useMemo((): HelpKeyFeature[] => {
    if (!content) return [];
    return content.functionality.keyFeatures.filter(
      (f) =>
        canShow(f.requiredPermission) &&
        (!filterText ||
          f.label.toLowerCase().includes(filterText) ||
          f.detail.toLowerCase().includes(filterText)),
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, filterText, hasPermission]);

  const filteredSteps = useMemo((): HelpStep[] => {
    if (!content) return [];
    return content.userGuide.steps.filter(
      (s) =>
        canShow(s.requiredPermission) &&
        (!filterText ||
          s.title.toLowerCase().includes(filterText) ||
          s.items.some((i) => i.text.toLowerCase().includes(filterText))),
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, filterText, hasPermission]);

  const filteredFaq = useMemo((): HelpFaqItem[] => {
    if (!content) return [];
    return content.faq.filter(
      (f) =>
        canShow(f.requiredPermission) &&
        (!filterText ||
          f.question.toLowerCase().includes(filterText) ||
          f.answer.toLowerCase().includes(filterText)),
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, filterText, hasPermission]);

  // Resolve page icon
  const PageIcon = content?.icon ? (ICON_MAP[content.icon] ?? HelpCircle) : HelpCircle;
  const iconBg = content?.iconColor ?? 'bg-blue-500';

  // ── No content state ────────────────────────────────────────────
  const NoContent: React.FC = () => (
    <div className="flex flex-col items-center justify-center py-16 text-center px-6">
      <HelpCircle className="h-12 w-12 text-muted-foreground mb-4" />
      <p className="text-sm text-muted-foreground">
        Help content for this page is coming soon.
      </p>
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[600px] p-0 flex flex-col overflow-hidden"
      >
        {/* ── Drawer Header ──────────────────────────────────────── */}
        <SheetHeader className="bg-muted/50 border-b px-5 py-4 shrink-0">
          <div className="flex items-start gap-3">
            <div className={cn('p-2.5 rounded-lg shrink-0', iconBg)}>
              <PageIcon className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <SheetTitle className="text-base font-semibold leading-tight">
                {content?.pageTitle ?? 'Help'}
              </SheetTitle>
              <SheetDescription className="text-xs mt-0.5 leading-snug">
                {content?.description ?? 'Help and guidance for this page.'}
              </SheetDescription>
            </div>
          </div>
          {/* Search */}
          <div className="relative mt-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Search help content…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-8 text-sm"
            />
          </div>
        </SheetHeader>

        {/* ── Tab Body ───────────────────────────────────────────── */}
        <div className="flex-1 overflow-hidden">
          {!content ? (
            <NoContent />
          ) : (
            <Tabs defaultValue="functionality" className="h-full flex flex-col">
              <TabsList className="mx-5 mt-4 shrink-0 w-[calc(100%-2.5rem)]">
                <TabsTrigger value="functionality" className="flex-1 gap-1.5">
                  <Info className="h-3.5 w-3.5" />
                  Functionality
                </TabsTrigger>
                <TabsTrigger value="guide" className="flex-1 gap-1.5">
                  <BookOpen className="h-3.5 w-3.5" />
                  User Guide
                </TabsTrigger>
                <TabsTrigger value="faq" className="flex-1 gap-1.5">
                  <HelpCircle className="h-3.5 w-3.5" />
                  FAQ
                </TabsTrigger>
              </TabsList>

              {/* ── Functionality Tab ─────────────────────────────── */}
              <TabsContent value="functionality" className="flex-1 overflow-y-auto px-5 py-4 space-y-6 mt-0">
                {/* Overview */}
                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Overview</h3>
                  <p className="text-sm text-foreground leading-relaxed">{content.functionality.overview}</p>
                </section>

                {/* Key Features */}
                {filteredFeatures.length > 0 && (
                  <section>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Key Features</h3>
                    <ul className="space-y-2.5">
                      {filteredFeatures.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2.5">
                          <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                          <span className="text-sm">
                            <span className="font-medium">{feature.label}</span>
                            {' — '}
                            <span className="text-muted-foreground">{feature.detail}</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {/* Sections & Fields */}
                {content.functionality.sectionsAndFields.length > 0 && (
                  <section>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Sections &amp; Fields</h3>
                    <div className="rounded-md border overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground w-2/5">Section / Field</th>
                            <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {content.functionality.sectionsAndFields
                            .filter((sf) => !filterText || sf.name.toLowerCase().includes(filterText) || sf.description.toLowerCase().includes(filterText))
                            .map((sf, i) => (
                              <tr key={i} className={cn('border-t', i % 2 === 0 ? 'bg-background' : 'bg-muted/20')}>
                                <td className="px-3 py-2 font-medium text-xs">{sf.name}</td>
                                <td className="px-3 py-2 text-xs text-muted-foreground">{sf.description}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}

                {/* Use Cases */}
                {content.functionality.useCases.length > 0 && (
                  <section>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Use Cases</h3>
                    <ul className="space-y-1.5">
                      {content.functionality.useCases
                        .filter((uc) => !filterText || uc.toLowerCase().includes(filterText))
                        .map((uc, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <ChevronRight className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground/60" />
                            {uc}
                          </li>
                        ))}
                    </ul>
                  </section>
                )}
              </TabsContent>

              {/* ── User Guide Tab ────────────────────────────────── */}
              <TabsContent value="guide" className="flex-1 overflow-y-auto px-5 py-4 space-y-6 mt-0">
                {/* Getting Started callout */}
                <div className="rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/40 p-3.5">
                  <div className="flex items-start gap-2">
                    <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-blue-800 dark:text-blue-300 mb-0.5">Getting Started</p>
                      <p className="text-sm text-blue-800 dark:text-blue-300">{content.userGuide.gettingStarted}</p>
                    </div>
                  </div>
                </div>

                {/* Step-by-step accordion */}
                {filteredSteps.length > 0 && (
                  <section>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Step-by-Step Guide</h3>
                    <Accordion type="multiple" className="space-y-1">
                      {filteredSteps.map((step, si) => (
                        <AccordionItem key={si} value={`step-${si}`} className="border rounded-md px-0">
                          <AccordionTrigger className="px-3 py-2.5 text-sm font-medium hover:no-underline">
                            {step.title}
                          </AccordionTrigger>
                          <AccordionContent className="px-3 pb-3">
                            <ol className="space-y-3 list-none">
                              {step.items.map((item, ii) => (
                                <li key={ii} className="text-sm">
                                  <div className="flex items-start gap-2">
                                    <Badge variant="outline" className="shrink-0 mt-0.5 h-5 w-5 flex items-center justify-center p-0 text-xs rounded-full">
                                      {ii + 1}
                                    </Badge>
                                    <span className="text-foreground">{renderBold(item.text)}</span>
                                  </div>
                                  {item.note && <NoteCallout text={item.note} />}
                                  {item.tip && <TipCallout text={item.tip} />}
                                  {item.subSteps && item.subSteps.length > 0 && (
                                    <ul className="mt-2 ml-7 space-y-1">
                                      {item.subSteps.map((sub, si2) => (
                                        <li key={si2} className="flex items-start gap-1.5 text-sm text-muted-foreground">
                                          <ChevronRight className="h-3 w-3 mt-0.5 shrink-0" />
                                          {sub}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </li>
                              ))}
                            </ol>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </section>
                )}

                {/* Pro Tips */}
                {content.userGuide.proTips.length > 0 && (
                  <section>
                    <div className="rounded-md bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 p-4">
                      <div className="flex items-center gap-2 mb-2.5">
                        <Lightbulb className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                        <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300">Pro Tips</h3>
                      </div>
                      <ul className="space-y-2">
                        {content.userGuide.proTips
                          .filter((pt) => !filterText || pt.toLowerCase().includes(filterText))
                          .map((pt, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-300">
                              <ChevronRight className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                              {pt}
                            </li>
                          ))}
                      </ul>
                    </div>
                  </section>
                )}
              </TabsContent>

              {/* ── FAQ Tab ───────────────────────────────────────── */}
              <TabsContent value="faq" className="flex-1 overflow-y-auto px-5 py-4 mt-0">
                {filteredFaq.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <HelpCircle className="h-8 w-8 text-muted-foreground mb-3" />
                    <p className="text-sm text-muted-foreground">
                      {filterText ? 'No FAQs match your search.' : 'No FAQs available for this page.'}
                    </p>
                  </div>
                ) : (
                  <Accordion type="multiple" className="space-y-1">
                    {filteredFaq.map((faq, i) => (
                      <AccordionItem key={i} value={`faq-${i}`} className="border rounded-md px-0">
                        <AccordionTrigger className="px-3 py-2.5 text-sm font-medium hover:no-underline text-left">
                          {faq.question}
                        </AccordionTrigger>
                        <AccordionContent className="px-3 pb-3">
                          <p className="text-sm text-muted-foreground mb-2">{faq.answer}</p>
                          {faq.steps && faq.steps.length > 0 && (
                            <ol className="mt-2 space-y-1.5 list-none">
                              {faq.steps.map((step, si) => (
                                <li key={si} className="flex items-start gap-2 text-sm text-muted-foreground">
                                  <Badge variant="outline" className="shrink-0 mt-0.5 h-5 w-5 flex items-center justify-center p-0 text-xs rounded-full">
                                    {si + 1}
                                  </Badge>
                                  {step}
                                </li>
                              ))}
                            </ol>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                )}
              </TabsContent>
            </Tabs>
          )}
        </div>

        {/* ── Footer: Related Pages ─────────────────────────────── */}
        {content && content.relatedPages.length > 0 && (
          <div className="bg-muted/50 border-t px-5 py-3 shrink-0">
            <p className="text-xs font-semibold text-muted-foreground mb-2">Related Pages</p>
            <div className="flex flex-wrap gap-2">
              {content.relatedPages.map((rp) => (
                <Button
                  key={rp.pageKey}
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => {
                    onNavigate(rp.pageKey);
                    onClose();
                  }}
                >
                  {rp.label}
                </Button>
              ))}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
